import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaCode, FaRobot, FaRocket, FaHeart } from 'react-icons/fa';
import { useLanguage } from '../utils/LanguageContext';
import './About.css';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const { t } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const features = [
    {
      icon: <FaCode />,
      title: t('about.features.fullStack.title'),
      description: t('about.features.fullStack.description')
    },
    {
      icon: <FaRobot />,
      title: t('about.features.ai.title'),
      description: t('about.features.ai.description')
    },
    {
      icon: <FaRocket />,
      title: t('about.features.api.title'),
      description: t('about.features.api.description')
    },
    {
      icon: <FaHeart />,
      title: t('about.features.devops.title'),
      description: t('about.features.devops.description')
    }
  ];

  return (
    <section id="about" className="section about-section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="section-header text-center">
            <h2 className="gradient-text">{t('about.title')}</h2>
            <p className="section-subtitle">
              {t('about.subtitle')}
            </p>
          </motion.div>

          <div className="about-content">
            <motion.div variants={itemVariants} className="about-text">
              <div className="card">
                <h3>{t('about.professionalSummary')}</h3>
                <p>
                  {t('about.summary1')}
                </p>
                <p>
                  {t('about.summary2')}
                </p>
                <p>
                  {t('about.summary3')}
                </p>

                <div className="personal-info">
                  <h4>{t('about.personalDetails')}</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>{t('about.location')}:</strong>
                      <span>{t('about.locationValue')}</span>
                    </div>
                    <div className="info-item">
                      <strong>{t('about.age')}:</strong>
                      <span>{t('about.ageValue')}</span>
                    </div>
                    <div className="info-item">
                      <strong>{t('about.languages')}:</strong>
                      <span>{t('about.languagesValue')}</span>
                    </div>
                    <div className="info-item">
                      <strong>{t('about.availability')}:</strong>
                      <span>{t('about.availabilityValue')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="about-features">
              <div className="features-grid">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="feature-card"
                    variants={itemVariants}
                    whileHover={{ 
                      scale: 1.05,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <div className="feature-icon">
                      {feature.icon}
                    </div>
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About; 